import { useEffect } from 'react';

import loadable from '@loadable/component';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';

import { logExperimentChokepoint, logMiniEvent } from '../../logMiniEvent';
import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import DealsTabs from './DealsTabs';
import FooterBlurb from './FooterBlurb/FooterBlurb';
import PriceBandSubtitle from './PriceBandSubtitle';
import getDealsCommonTrackingProps from './getDealsCommonTrackingProps';

import type { DealsEvolvedProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';

import STYLES from './DealsEvolved.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const DealsEvolvedWithTabs = (props: DealsEvolvedProps) => {
  useEffect(() => {
    if (
      props.trackingContext.pageType === 'flights-home' ||
      props.trackingContext.pageType === 'last-minute-deals'
    ) {
      logExperimentChokepoint('users_who_saw_destination_images_with_coverage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = rawComponentName;
  const {
    deals,
    dealsLimit,
    priceBandSubtitle,
    priceBandsDisplayState,
    strings,
  } = props;

  return (
    <div
      className={STYLES.DealsEvolved}
      id="deals-evolved"
      data-testid="dealsEvolved"
      data-tracking-element-id={mbdComponentName}
      data-tracking-common-params={buildMBDParam(mbdComponentName, {
        number_of_deals_requested: dealsLimit.toString(),
        number_of_deals_returned: deals.length.toString(),
        price_bands_display_state: priceBandsDisplayState,
      })}
    >
      <div className={STYLES.DealsEvolved__header}>
        <BpkSectionHeader
          title={strings.header}
          description={strings.subheader}
        />
      </div>
      {priceBandSubtitle && (
        <PriceBandSubtitle priceBandSubtitle={priceBandSubtitle} />
      )}
      <DealsTabs {...props} />
      <FooterBlurb disclaimer={strings.disclaimer} />
    </div>
  );
};

const DealsEvolvedWithTabsWithErrorBoundary = withErrorBoundary(
  DealsEvolvedWithTabs,
  'deals-evolved-component',
);

const DealsEvolvedWithTabsWrapped = (props: DealsEvolvedProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEvent('DealsEvolvedEvent', 'deals_evolved_component', {
        deals_common: getDealsCommonTrackingProps(props),
        event_type: 'COMPONENT_SEEN',
      })
    }
  >
    <DealsEvolvedWithTabsWithErrorBoundary {...props} />
  </IntersectionObserverWrapper>
);

export default DealsEvolvedWithTabsWrapped;
